import { fetchUserAttributes, getCurrentUser } from "@aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createConnectionRequest,
  createNotifications,
  createUserLink,
  createUserPayments,
  updateUserLink,
  updateConnectionRequest,
  updateGeneralInformation,
  updateUserPayments,
} from "../graphql/mutations.js";
import {
  listConnectionRequests,
  listGeneralInformations,
  listOrganizationCPs,
  listUserLinks,
  listUserPayments, // ✅ make sure this is imported!
} from "../graphql/queries.js";
import {
  onCreateConnectionRequest,
  onDeleteConnectionRequest,
  onUpdateConnectionRequest,
} from "../graphql/subscriptions.js";
import { postEmailNotification } from "../utils/postEmailNotification.js";
const client = generateClient();

const LIST_USER_LINKS_WITH_PRO_ROLE = /* GraphQL */ `
  query ListUserLinks(
    $filter: ModelUserLinkFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listUserLinks(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        organizationId
        professionalId
        professionalEmail
        professionalFirstName
        professionalLastName
        professionalRole
        clientId
        clientEmail
        clientFirstName
        clientLastName
        linkedAt
        unlinkedAt
        clientViewable
        subscriptionId
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

async function setCognitoOrganizationIdViaInviteLambda(email, organizationId) {
  if (!email || !organizationId) return;
  const url =
    "https://kpwdaqkfpck6zvyr67a3eatm6i0ywoup.lambda-url.us-east-1.on.aws/";
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "setOrganizationId",
        email,
        organizationId,
      }),
    });
  } catch (err) {
    console.error("Failed to set client Cognito organizationId:", err);
  }
}

// Minimal debug wrapper for GraphQL calls — logs only on error
const debugGraphql = async (label, query, variables) => {
  try {
    return await client.graphql({ query, variables });
  } catch (err) {
    console.error(`[GraphQL:${label}]`, err);
    return { __error: err };
  }
};

const checkOrgClientLimit = async (arg1, arg2) => {
  const today = new Date().toISOString();

  const context =
    arg1 && typeof arg1 === "object"
      ? arg1
      : { professionalEmail: arg1, ...(arg2 || {}) };

  const professionalEmail = String(context.professionalEmail || "")
    .trim()
    .toLowerCase();
  const professionalId = context.professionalId || null;
  let organizationId = null;
  let isOrganizationEmployee = false;

  // The organization membership record, not a potentially stale Cognito claim,
  // determines whether a professional uses organization client capacity.
  if (professionalEmail) {
    const membershipRes = await debugGraphql(
      "listOrganizationCPs.byProfessionalEmail",
      listOrganizationCPs,
      {
        filter: { OrganizationCPEmail: { eq: professionalEmail } },
        limit: 1,
      },
    );
    const membership =
      membershipRes?.data?.listOrganizationCPs?.items?.[0] || null;
    let resolvedMembership = membership;

    // Employee records created before email normalization may have mixed case.
    // Fall back to the visible membership list and compare normalized emails.
    if (!resolvedMembership) {
      const membershipsRes = await debugGraphql(
        "listOrganizationCPs.normalizedProfessionalEmail",
        listOrganizationCPs,
        { limit: 1000 },
      );
      resolvedMembership = (
        membershipsRes?.data?.listOrganizationCPs?.items || []
      ).find(
        (record) =>
          String(record?.OrganizationCPEmail || "")
            .trim()
            .toLowerCase() === professionalEmail,
      );
    }

    if (resolvedMembership?.OrganizationId) {
      organizationId = resolvedMembership.OrganizationId;
      isOrganizationEmployee = true;
    }
  }

  const paymentUserId = organizationId || professionalId;
  console.log("🔍 Running client limit check", {
    professionalEmail,
    professionalId,
    organizationId,
    isOrganizationEmployee,
    paymentUserId,
  });

  if (!paymentUserId) {
    throw new Error(
      isOrganizationEmployee
        ? "Your organization's client coverage could not be verified. Please see your organization's administrator to ensure there is adequate coverage for adding clients."
        : "Client coverage is required to accept this request. Switch to the Professional view, then open Payment Information to add coverage for clients.",
    );
  }

  // 1) Get active payments for the payer scope (org or professional)
  const paymentsRes = await debugGraphql(
    "listUserPayments.activeByScope",
    listUserPayments,
    {
      filter: {
        and: [
          { paidUntil: { gt: today } },
          {
            or: [
              { paymentStatus: { eq: "active" } },
              { paymentStatus: { eq: "completed" } },
            ],
          },
          { userId: { eq: paymentUserId } },
        ],
      },
      limit: 50,
    },
  );

  const payments = paymentsRes?.data?.listUserPayments?.items || [];
  if (paymentsRes?.__error && isOrganizationEmployee) {
    throw new Error(
      "Unable to verify your organization's client availability. Sign out and sign back in to refresh your organization access, then contact your organization's administrator if the issue continues.",
    );
  }
  const maxClients = payments.reduce(
    (sum, p) => sum + (p.clientsProjected || 0),
    0,
  );

  // 2) Count current linked clients for this scope
  const linkFilter = organizationId
    ? {
        and: [
          { organizationId: { eq: organizationId } },
          { unlinkedAt: { attributeExists: false } },
        ],
      }
    : {
        and: [
          { professionalId: { eq: professionalId } },
          { unlinkedAt: { attributeExists: false } },
        ],
      };

  const linksRes = await debugGraphql("listUserLinks.byScope", listUserLinks, {
    filter: linkFilter,
    limit: 1000,
  });
  const links = linksRes?.data?.listUserLinks?.items;
  if (!Array.isArray(links)) {
    throw new Error(
      "Unable to enforce client limits right now (missing permission to read current connections).",
    );
  }

  const currentClientCount = new Set(
    links.map((l) =>
      String(l?.clientEmail || "")
        .trim()
        .toLowerCase(),
    ),
  ).size;

  console.log("💰 Active payments found:", payments.length);
  console.log("🧮 Max allowed clients from payments:", maxClients);
  console.log("🔗 Current unique linked clients:", currentClientCount);

  if (maxClients <= 0) {
    throw new Error(
      isOrganizationEmployee
        ? "Your organization does not have adequate client coverage to add this client. Please see your organization's administrator."
        : "Client coverage is required to accept this request. Switch to the Professional view, then open Payment Information to add coverage for clients.",
    );
  }

  if (currentClientCount >= maxClients) {
    console.warn("🚫 Limit reached! Blocking new connection.");
    throw new Error(
      isOrganizationEmployee
        ? "Your organization does not have adequate client coverage to add this client. Please see your organization's administrator."
        : "Client coverage is required to accept this request. Switch to the Professional view, then open Payment Information to add coverage for clients.",
    );
  }

  return { organizationId };
};

export const getOrgClientStats = async (professionalEmail) => {
  const today = new Date().toISOString();

  // 1. Find org
  const proEmailNorm = String(professionalEmail || "")
    .trim()
    .toLowerCase();
  if (!proEmailNorm) return null;

  const orgCPRes = await debugGraphql(
    "listOrganizationCPs.byEmail",
    listOrganizationCPs,
    {
      filter: { OrganizationCPEmail: { eq: proEmailNorm } },
      limit: 1,
    },
  );

  const proCP = orgCPRes?.data?.listOrganizationCPs?.items?.[0];
  if (!proCP) return null;

  const orgId = proCP.OrganizationId;

  // 2. Count active payments
  const paymentsRes = await debugGraphql(
    "listUserPayments.activeByOrg",
    listUserPayments,
    {
      filter: {
        and: [
          { paidUntil: { gt: today } },
          {
            or: [
              { paymentStatus: { eq: "active" } },
              { paymentStatus: { eq: "completed" } },
            ],
          },
          {
            or: [
              { organizationId: { contains: orgId } },
              { userId: { eq: orgId } },
            ],
          },
        ],
      },
    },
  );

  const payments = paymentsRes?.data?.listUserPayments?.items || [];
  const maxClients = payments.reduce(
    (sum, p) => sum + (p.clientsProjected || 0),
    0,
  );

  // 3. Get all professionals under org
  const allCPsRes = await debugGraphql(
    "listOrganizationCPs.all",
    listOrganizationCPs,
  );
  const allCpsItems = allCPsRes?.data?.listOrganizationCPs?.items;
  if (!Array.isArray(allCpsItems)) return null;

  const professionalsInOrg = allCpsItems
    .filter((cp) => cp.OrganizationId === orgId)
    .map((cp) => cp.OrganizationCPEmail);

  // 4. Count unique linked clients
  const userLinksRes = await debugGraphql("listUserLinks.all", listUserLinks);
  const userLinksItems = userLinksRes?.data?.listUserLinks?.items;
  if (!Array.isArray(userLinksItems)) return null;
  const userLinks = userLinksItems;

  const linkedClients = userLinks
    .filter((l) => professionalsInOrg.includes(l.professionalEmail))
    .map((l) => l.clientEmail);

  const currentCount = new Set(linkedClients).size;

  return { current: currentCount, max: maxClients };
};

export default function ManageConnections() {
  const containerRef = useRef(null);
  const sendingRequestRef = useRef(false);
  const [professionalRoles, setProfessionalRoles] = useState({}); // userId => role

  // Backend compatibility: `professionalRole` is a newly added field on UserLink.
  // If the deployed AppSync schema doesn't have it yet, querying it will fail.
  // We detect support at runtime and gracefully fall back.
  const [
    userLinkSupportsProfessionalRole,
    setUserLinkSupportsProfessionalRole,
  ] = useState(null); // null | boolean

  const [targetEmail, setTargetEmail] = useState("");
  const [direction, setDirection] = useState("");
  const [notification, setNotification] = useState("");
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [requesterEmail, setRequesterEmail] = useState("");
  const [requesterId, setRequesterId] = useState("");
  const [signedInAs, setSignedInAs] = useState("");
  const [userLinks, setUserLinks] = useState([]);
  const [linkToUnlink, setLinkToUnlink] = useState(null);
  const [requests, setRequests] = useState([]);
  const [pastrequests, setPastRequests] = useState([]);
  const [orgClientStats, setOrgClientStats] = useState(null);
  const [accountType, setAccountType] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [modalCenterX, setModalCenterX] = useState(() => {
    if (typeof window === "undefined") return 0;
    return Math.round(window.innerWidth / 2);
  });

  // Role lookup by stable identity (Cognito sub / `userId`).
  const fetchProfessionalRoles = useCallback(async (professionalIds) => {
    const rolesMap = {};

    await Promise.all(
      professionalIds.map(async (id) => {
        if (!id) return;

        try {
          // First attempt: lookup by owner or userId
          const res = await client.graphql({
            query: listGeneralInformations,
            variables: {
              filter: {
                or: [{ owner: { eq: id } }, { userId: { eq: id } }],
              },
              limit: 1,
            },
          });

          const items = res?.data?.listGeneralInformations?.items || [];
          let role = items[0]?.ProfessionalRole;

          // NOTE: Intentionally no email-based fallback here.
          // Emails can change; userId/sub should be the stable join key.

          // Important: don't store the string "Unknown" here.
          // If we store it, it becomes truthy and can override real role
          // information we already have via `GeneralInformation`.
          if (role) rolesMap[id] = role;
        } catch (error) {
          console.error(`Error fetching role for userId ${id}:`, error);
          // Leave unset so UI can fall back to other sources.
        }
      }),
    );

    setProfessionalRoles(rolesMap);
  }, []);

  const fetchUserLinks = useCallback(
    async (userId, role, accountType) => {
      try {
        let data = null;

        // Try querying the new field if we don't know support yet (or we know it's supported).
        if (userLinkSupportsProfessionalRole !== false) {
          try {
            const res = await client.graphql({
              query: LIST_USER_LINKS_WITH_PRO_ROLE,
            });
            data = res?.data || null;
            if (userLinkSupportsProfessionalRole !== true) {
              setUserLinkSupportsProfessionalRole(true);
            }
          } catch (e) {
            // If the backend schema doesn't have the field yet (or AppSync returns a
            // validation error), fall back to the legacy query rather than failing.
            if (isUnknownFieldError(e, "professionalRole")) {
              setUserLinkSupportsProfessionalRole(false);
            } else {
              const msg = String(e?.errors?.[0]?.message || e?.message || "");
              if (
                msg.toLowerCase().includes("validation") ||
                msg.toLowerCase().includes("cannot query field")
              ) {
                setUserLinkSupportsProfessionalRole(false);
              } else {
                // Non-schema related error (network/auth/etc). We'll let it surface.
                throw e;
              }
            }
          }
        }

        // Legacy fallback (deployed schema doesn't have professionalRole yet)
        if (!data) {
          try {
            const resLegacy = await client.graphql({
              query: listUserLinks,
            });
            data = resLegacy?.data || null;
          } catch (e) {
            console.error("Legacy listUserLinks failed:", e);
            setNotification("Unable to load connections right now.");
            return;
          }
        }

        const links = data?.listUserLinks?.items || [];
        console.info("fetchUserLinks: fetched links count", links.length, {
          userId,
          role,
          accountType,
        });
        console.debug("fetchUserLinks: links sample", links.slice(0, 5));

        // A connection is visible to either participant. Role attributes can be
        // temporarily stale after an account switch, so they must not decide
        // which side of the link the signed-in user may see.
        const filteredLinks = links.filter((link) => {
          return link.professionalId === userId || link.clientId === userId;
        });

        setUserLinks(filteredLinks);

        const professionalIds = filteredLinks
          .filter((link) => link.professionalId)
          .map((link) => link.professionalId);

        if (role === "Professional") {
          await fetchProfessionalRoles(professionalIds);
        }

        // Backfill `professionalRole` onto links (only when backend supports it).
        if (
          role === "Professional" &&
          userLinkSupportsProfessionalRole === true
        ) {
          const linksMissingRole = filteredLinks.filter(
            (l) => !l?.professionalRole && !l?.unlinkedAt,
          );
          if (linksMissingRole.length > 0) {
            try {
              const giRes = await client.graphql({
                query: listGeneralInformations,
                variables: {
                  filter: {
                    or: [{ userId: { eq: userId } }, { owner: { eq: userId } }],
                  },
                  limit: 1,
                },
              });

              const myRole =
                giRes?.data?.listGeneralInformations?.items?.[0]
                  ?.ProfessionalRole || null;

              if (myRole) {
                await Promise.all(
                  linksMissingRole.map((l) =>
                    client.graphql({
                      query: updateUserLink,
                      variables: {
                        input: {
                          id: l.id,
                          professionalRole: myRole,
                        },
                      },
                    }),
                  ),
                );

                setUserLinks((prev) =>
                  (prev || []).map((l) =>
                    linksMissingRole.some((m) => m.id === l.id)
                      ? { ...l, professionalRole: myRole }
                      : l,
                  ),
                );
              }
            } catch (e) {
              console.warn("Could not backfill professionalRole on links:", e);
            }
          }
        }
      } catch (error) {
        console.error("Caught error in fetchUserLinks:", error);
      }
    },
    [fetchProfessionalRoles, userLinkSupportsProfessionalRole],
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!linkToUnlink || typeof document === "undefined") return;

    const updateModalCenterX = () => {
      const rect = containerRef.current?.getBoundingClientRect?.();
      if (
        !rect ||
        !Number.isFinite(rect.left) ||
        !Number.isFinite(rect.width)
      ) {
        setModalCenterX(Math.round(window.innerWidth / 2));
        return;
      }

      setModalCenterX(Math.round(rect.left + rect.width / 2 + 16));
    };

    updateModalCenterX();

    // Track sidebar transition/layout shifts while the modal is visible.
    const intervalId = window.setInterval(updateModalCenterX, 50);
    window.addEventListener("resize", updateModalCenterX);

    return () => {
      window.removeEventListener("resize", updateModalCenterX);
      window.clearInterval(intervalId);
    };
  }, [linkToUnlink]);

  const isUnknownFieldError = (err, fieldName) => {
    const msg = String(err?.errors?.[0]?.message || err?.message || "");
    return (
      msg.includes(`Cannot query field "${fieldName}"`) ||
      msg.includes(`Field "${fieldName}"`) ||
      msg.includes(`is not defined by type`) ||
      msg.toLowerCase().includes("cannot query field") ||
      msg.toLowerCase().includes("fieldundefined") ||
      msg.toLowerCase().includes("validation error")
    );
  };
  const containerStyle2 = {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: isMobile
      ? "min(350px, calc(100vw - 24px))"
      : "min(1000px, calc(100vw - 64px))",
    margin: "10px 0",
    padding: 24,
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    boxSizing: "border-box",
  };
  const summaryRowStyle = {
    ...linkBoxStyle,
    width: "100%",
    alignItems: "stretch",
    boxSizing: "border-box",
    overflow: "hidden",
  };
  const connectionGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "minmax(0, 2.1fr) minmax(0, 1.7fr) minmax(0, 1.2fr) auto",
    gap: 16,
    width: "100%",
    alignItems: "center",
  };
  const requestGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "minmax(0, 2.2fr) minmax(0, 1.4fr) minmax(0, 1.2fr) auto",
    gap: 16,
    width: "100%",
    alignItems: "center",
  };
  const historyGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "minmax(0, 2.1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)",
    gap: 16,
    width: "100%",
    alignItems: "center",
  };
  const summaryCellStyle = {
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const summaryValueStyle = {
    marginTop: 4,
    color: "#1f2937",
  };
  const summaryActionStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    justifyContent: isMobile ? "flex-start" : "flex-end",
  };
  const cancelRequest = async (requestId) => {
    try {
      // Update the notification's Status to "Cancelled"
      await client.graphql({
        query: updateConnectionRequest,
        variables: {
          input: {
            id: requestId,
            status: "cancelled", // <-- set the status
          },
        },
      });

      // Update locally so UI reflects the change
      setRequests((prev) => prev.filter((r) => r.id !== requestId));

      await fetchConnectionRequests(requesterEmail, signedInAs, requesterId);
    } catch (err) {
      console.error("Failed to cancel request:", err);
    }
  };
  const fetchConnectionRequests = useCallback(async (email, role, userId) => {
    try {
      const normalizedEmail = String(email || "")
        .trim()
        .toLowerCase();

      if (!normalizedEmail || !userId) {
        setRequests([]);
        setPastRequests([]);
        return;
      }

      const pendingOrClientAccepted = {
        or: [
          { status: { eq: "pending" } },
          { status: { eq: "clientAccepted" } },
        ],
      };

      const [incomingRes, outgoingRes, historyRes] = await Promise.all([
        client.graphql({
          query: listConnectionRequests,
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { targetUserId: { eq: userId } },
                    { targetEmail: { eq: normalizedEmail } },
                  ],
                },
                pendingOrClientAccepted,
              ],
            },
          },
        }),
        client.graphql({
          query: listConnectionRequests,
          variables: {
            filter: {
              requesterEmail: { eq: normalizedEmail },
              ...pendingOrClientAccepted,
            },
          },
        }),
        client.graphql({
          query: listConnectionRequests,
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { requesterEmail: { eq: normalizedEmail } },
                    { targetEmail: { eq: normalizedEmail } },
                    { targetUserId: { eq: userId } },
                  ],
                },
                { status: { ne: "pending" } },
                { status: { ne: "clientAccepted" } },
              ],
            },
          },
        }),
      ]);

      const incomingRequests =
        incomingRes?.data?.listConnectionRequests?.items || [];
      const outgoingRequests =
        outgoingRes?.data?.listConnectionRequests?.items || [];
      const historyRequests =
        historyRes?.data?.listConnectionRequests?.items || [];

      const sortByLatest = (items) =>
        [...items]
          .filter(Boolean)
          .sort(
            (left, right) =>
              new Date(right.updatedAt || right.createdAt || 0).getTime() -
              new Date(left.updatedAt || left.createdAt || 0).getTime(),
          );

      const combinedRequests = Array.from(
        new Map(
          [...incomingRequests, ...outgoingRequests].map((item) => [
            item.id,
            item,
          ]),
        ).values(),
      );

      setRequests(sortByLatest(combinedRequests));
      setPastRequests(sortByLatest(historyRequests));
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  }, []);

  const respondToRequest = async (request, accept) => {
    const isToPro = request.direction === "toProfessional";
    const professionalEmail = isToPro
      ? request.targetEmail
      : request.requesterEmail;
    const user = await fetchUserAttributes();
    const userId = user.sub;
    const professionalEmailNorm = String(professionalEmail || "")
      .trim()
      .toLowerCase();
    const responderOrgId = user["custom:organizationId"] || null;
    const requestOrgId = request?.organizationId || null;
    const organizationId = requestOrgId || responderOrgId || null;

    const isResponderProfessional = signedInAs === "Professional";
    const isClientRequestForResponder =
      isToPro &&
      String(request.targetEmail || "")
        .trim()
        .toLowerCase() ===
        String(user.email || "")
          .trim()
          .toLowerCase();
    const isProfessionalConfirmingClientAcceptance =
      !isToPro &&
      request.status === "clientAccepted" &&
      String(request.requesterEmail || "")
        .trim()
        .toLowerCase() ===
        String(user.email || "")
          .trim()
          .toLowerCase();

    if (accept && isClientRequestForResponder && !isResponderProfessional) {
      alert(
        "To accept a client connection, switch to the Professional view, then open Payment Information to add coverage for clients.",
      );
      return;
    }

    // Enforce org client limits only when the *professional* is accepting.
    // (Individuals accepting a pro's request should never need org-wide reads.)
    if (
      accept &&
      (isClientRequestForResponder ||
        isProfessionalConfirmingClientAcceptance) &&
      isResponderProfessional &&
      professionalEmailNorm
    ) {
      try {
        await checkOrgClientLimit({
          professionalEmail: professionalEmailNorm,
          professionalId: userId,
          organizationId: responderOrgId || requestOrgId || null,
        });
      } catch (err) {
        console.error("❌ Client limit error:", err.message);
        alert(err.message);
        return;
      }
    }

    try {
      // 1. Update connection request status
      await client.graphql({
        query: updateConnectionRequest,
        variables: {
          input: {
            id: request.id,
            status: accept ? "accepted" : "declined",
            ...(accept &&
            isResponderProfessional &&
            organizationId &&
            !requestOrgId
              ? { organizationId }
              : {}),
          },
        },
      });

      // 2. Create notification for the other party
      const attrs = await fetchUserAttributes();
      const senderFirstName = attrs["given_name"] || "";
      const senderLastName = attrs["family_name"] || "";
      const senderEmail = attrs["email"] || "";
      const senderId = attrs["sub"] || "";

      // organizationId is resolved without querying other users' OrganizationCPs.

      // Notification target is the other person (not sender)
      const isToPro = request.direction === "toProfessional";

      const senderEmailNormalized = (senderEmail || "").trim().toLowerCase();
      // Determine who the responder is:
      const responderEmail = senderEmailNormalized; // current user (who is responding)

      // The notification should go to the **other** person:
      let notificationTargetEmail = null;
      let notificationTargetUserId = null;

      if (responderEmail === request.requesterEmail.toLowerCase()) {
        // If responder is the requester, notify the target
        notificationTargetEmail = request.targetEmail.toLowerCase();
        notificationTargetUserId = request.targetUserId || null;
      } else if (responderEmail === request.targetEmail.toLowerCase()) {
        // If responder is the target, notify the requester
        notificationTargetEmail = request.requesterEmail.toLowerCase();
        notificationTargetUserId = request.requesterId || null;
      } else {
        // fallback, just send to target
        notificationTargetEmail = request.targetEmail.toLowerCase();
        notificationTargetUserId = request.targetUserId || null;
      }

      const notificationInput = {
        SenderId: senderId,
        SenderFirstName: senderFirstName,
        SenderLastName: senderLastName,
        SenderEmail: senderEmailNormalized,
        TargetEmail: notificationTargetEmail,
        TargetUserId: notificationTargetUserId,
        NotificationType: "ConnectionResponse",
        Status: "pending",
        StatusDate: new Date().toISOString().split("T")[0], // YYYY-MM-DD
        RecommendationNote: accept ? "Accepted" : "Declined",
        ...(organizationId ? { organizationId } : {}),
      };
      // notification prepared

      await client.graphql({
        query: createNotifications,
        variables: { input: notificationInput },
      });

      // 3. If accepted, create user link
      if (accept) {
        const isResponderTarget =
          request.targetEmail.toLowerCase() === user.email.toLowerCase();
        // Ensure we have actual user IDs for requester and target.
        let targetUserId = request.targetUserId || null;
        let requesterUserId = request.requesterId || null;

        const requesterEmailNorm = (request.requesterEmail || "")
          .trim()
          .toLowerCase();
        const targetEmailNorm = (request.targetEmail || "")
          .trim()
          .toLowerCase();
        if (!requesterUserId && requesterEmailNorm === senderEmailNormalized) {
          requesterUserId = userId;
        }
        if (!targetUserId && targetEmailNorm === senderEmailNormalized) {
          targetUserId = userId;
        }

        // If targetId is missing, try to look it up by email (handles requests created without targetId)
        if (!targetUserId && request.targetEmail) {
          try {
            const found = await client.graphql({
              query: listGeneralInformations,
              variables: {
                filter: {
                  Email: { eq: request.targetEmail.trim().toLowerCase() },
                },
                limit: 1,
              },
            });
            targetUserId =
              found?.data?.listGeneralInformations?.items?.[0]?.userId || null;
          } catch (e) {
            console.warn("Could not lookup target userId by email:", e);
          }
        }

        // If requesterId missing, try to lookup as well
        if (!requesterUserId && request.requesterEmail) {
          try {
            const foundReq = await client.graphql({
              query: listGeneralInformations,
              variables: {
                filter: {
                  Email: { eq: request.requesterEmail.trim().toLowerCase() },
                },
                limit: 1,
              },
            });
            requesterUserId =
              foundReq?.data?.listGeneralInformations?.items?.[0]?.userId ||
              null;
          } catch (e) {
            console.warn("Could not lookup requester userId by email:", e);
          }
        }

        console.info("Responder identity", {
          userId,
          cognitoSub: senderId,
          responderEmail: senderEmailNormalized,
        });
        console.info("UserLink input prepared (before create)");

        // Stamp role onto the link for Individual visibility (only if backend supports it).
        let professionalRoleToStamp = null;
        if (userLinkSupportsProfessionalRole === true) {
          try {
            const giRes = await client.graphql({
              query: listGeneralInformations,
              variables: {
                filter: {
                  or: [{ userId: { eq: userId } }, { owner: { eq: userId } }],
                },
                limit: 1,
              },
            });
            professionalRoleToStamp =
              giRes?.data?.listGeneralInformations?.items?.[0]
                ?.ProfessionalRole || null;
          } catch (e) {
            console.warn(
              "Could not fetch professional role for link stamp:",
              e,
            );
          }
        }

        const input = {
          ...(organizationId ? { organizationId } : {}),
          professionalId: isToPro
            ? isResponderTarget
              ? userId
              : targetUserId
            : requesterUserId,
          clientId: isToPro
            ? requesterUserId
            : isResponderTarget
              ? userId
              : targetUserId,

          professionalFirstName: isToPro
            ? isResponderTarget
              ? user.given_name
              : request.targetFirstName
            : request.requesterFirstName,
          professionalLastName: isToPro
            ? isResponderTarget
              ? user.family_name
              : request.targetLastName
            : request.requesterLastName,

          clientFirstName: isToPro
            ? request.requesterFirstName
            : isResponderTarget
              ? user.given_name
              : request.targetFirstName,
          clientLastName: isToPro
            ? request.requesterLastName
            : isResponderTarget
              ? user.family_name
              : request.targetLastName,

          professionalEmail: isToPro
            ? request.targetEmail
            : request.requesterEmail,
          clientEmail: isToPro ? request.requesterEmail : request.targetEmail,
          linkedAt: new Date().toISOString(),
          clientViewable: true,
          ...(professionalRoleToStamp
            ? { professionalRole: professionalRoleToStamp }
            : {}),
        };

        if (!input.professionalId || !input.clientId) {
          console.error("UserLink create blocked: missing required IDs", {
            input,
            request,
            userId,
            senderEmailNormalized,
          });
          throw new Error("Unable to create connection link: missing user IDs");
        }

        // Guard against double-creating the same link (can happen if a request is
        // confirmed twice or if the client accepted earlier).
        try {
          const existingLinkRes = await client.graphql({
            query: listUserLinks,
            variables: {
              filter: {
                and: [
                  { professionalId: { eq: input.professionalId } },
                  { clientId: { eq: input.clientId } },
                  { unlinkedAt: { attributeExists: false } },
                ],
              },
              limit: 1,
            },
          });
          const existingActiveLink =
            existingLinkRes?.data?.listUserLinks?.items?.[0] || null;
          if (existingActiveLink?.id) {
            console.info("UserLink already exists; skipping create", {
              id: existingActiveLink.id,
            });
          } else {
            console.info(input);
            const createRes = await client.graphql({
              query: createUserLink,
              variables: { input },
            });
            console.info("createUserLink response:", createRes);
          }
        } catch (e) {
          // If we can't verify existing links, fall back to attempting create.
          console.warn(
            "Could not check for existing UserLink; creating anyway",
            e,
          );
          console.info(input);
          const createRes = await client.graphql({
            query: createUserLink,
            variables: { input },
          });
          console.info("createUserLink response:", createRes);
        }

        if (organizationId && input.clientEmail) {
          await setCognitoOrganizationIdViaInviteLambda(
            String(input.clientEmail).trim().toLowerCase(),
            organizationId,
          );
        }

        if (organizationId && input.clientId) {
          try {
            const giRes = await client.graphql({
              query: listGeneralInformations,
              variables: {
                filter: {
                  or: [
                    { userId: { eq: input.clientId } },
                    { owner: { eq: input.clientId } },
                  ],
                },
                limit: 1,
              },
            });
            const giItem = giRes?.data?.listGeneralInformations?.items?.[0];
            if (giItem?.id && giItem.organizationId !== organizationId) {
              await client.graphql({
                query: updateGeneralInformation,
                variables: {
                  input: { id: giItem.id, organizationId },
                },
              });
            }
          } catch (e) {
            console.warn(
              "Failed to stamp organizationId on client GeneralInformation:",
              e,
            );
          }
        }

        // Grant client access if professional has paid
        try {
          const professionalId = input.professionalId;
          const clientId = input.clientId;

          // Use org ID if present on the link/request; otherwise use professional ID
          const paymentUserId = organizationId || professionalId;

          // Check if professional/org has active payments
          const today = new Date().toISOString();
          const proPaymentsRes = await client.graphql({
            query: listUserPayments,
            variables: {
              filter: {
                and: [
                  { userId: { eq: paymentUserId } },
                  { paidUntil: { gt: today } },
                  {
                    or: [
                      { paymentFor: { eq: "professional" } },
                      { paymentFor: { eq: "Professional" } },
                      { paymentFor: { eq: "organization" } },
                    ],
                  },
                ],
              },
              limit: 50,
            },
          });

          const proPayments =
            proPaymentsRes?.data?.listUserPayments?.items || [];
          const activeProPayment = proPayments.find(
            (p) => (p.clientsProjected || 0) > 0,
          );

          if (activeProPayment) {
            console.log(
              `✅ ${organizationId ? "Organization" : "Professional"} has active payment with clients - granting client access`,
            );

            // Reduce the clientsProjected count
            const newClientsProjected = Math.max(
              0,
              (activeProPayment.clientsProjected || 0) - 1,
            );
            await client.graphql({
              query: updateUserPayments,
              variables: {
                input: {
                  id: activeProPayment.id,
                  clientsProjected: newClientsProjected,
                },
              },
            });
            console.log(
              `✅ Reduced clientsProjected from ${activeProPayment.clientsProjected} to ${newClientsProjected}`,
            );

            // Check if client already has payment record
            const clientPaymentsRes = await client.graphql({
              query: listUserPayments,
              variables: {
                filter: {
                  or: [
                    { owner: { eq: clientId } },
                    { userId: { eq: clientId } },
                  ],
                },
                limit: 50,
              },
            });

            const clientPayments =
              clientPaymentsRes?.data?.listUserPayments?.items || [];
            const existingClientPayment = clientPayments.find(
              (p) => p.paymentFor === "Individual",
            );

            const paidUntilDate = new Date();
            paidUntilDate.setFullYear(paidUntilDate.getFullYear() + 1);

            if (existingClientPayment) {
              // Update existing payment
              await client.graphql({
                query: updateUserPayments,
                variables: {
                  input: {
                    id: existingClientPayment.id,
                    userId: clientId,
                    paidUntil: paidUntilDate.toISOString(),
                    paymentFor: "Individual",
                  },
                },
              });
              console.log("✅ Updated client payment record");
            } else {
              // Create new payment
              await client.graphql({
                query: createUserPayments,
                variables: {
                  input: {
                    userId: clientId,
                    paidUntil: paidUntilDate.toISOString(),
                    paymentFor: "Individual",
                  },
                },
              });
              console.log("✅ Created new client payment record");
            }
          } else {
            console.log(
              "ℹ️ Professional does not have active payment with clients",
            );
          }
        } catch (err) {
          console.error("Error granting client access:", err);
          // Don't fail the whole operation if this fails
        }
      }

      // 4. Refresh requests to move accepted/declined to history
      const attrs2 = await fetchUserAttributes();
      const email2 = attrs2.email || "";
      const role2 =
        attrs2["custom:SignedInAs"] || attrs2["custom:accountType"] || "";
      await fetchConnectionRequests(email2, role2, attrs2.sub);

      // 5. Update state and refresh links for the current user
      // use the actual responder's id (userId) so both sides see the update
      await fetchUserLinks(userId, signedInAs, accountType);

      // 6. Refresh org client stats if professional
      if (signedInAs === "Professional") {
        const stats = await getOrgClientStats(email2);
        if (stats) setOrgClientStats(stats);
      }
    } catch (err) {
      console.error("Error responding to request:", err);
    }
  };
  // pastrequests already contains non-pending requests from the query
  const historyRequests = pastrequests;

  useEffect(() => {
    async function fetchUserInfo() {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        // signInDetails.loginId is only reliably populated right after an
        // interactive sign-in - on a page refresh/rehydrated session it can
        // be undefined, which silently short-circuits fetchConnectionRequests.
        // fetchUserAttributes().email is stable across refreshes.
        const email = attributes.email || user.signInDetails?.loginId;
        const uid = attributes.sub;

        const role =
          attributes["custom:SignedInAs"] ||
          attributes["custom:accountType"] ||
          "";
        const acctType = attributes["custom:accountType"] || "";

        setAccountType(acctType);
        setRequesterId(uid);
        setRequesterEmail(email);
        setSignedInAs(role);

        const dir = role === "Professional" ? "toIndividual" : "toProfessional";
        setDirection(dir);

        // Pass acctType directly here to avoid stale state issue
        await fetchUserLinks(uid, role, acctType);
        await fetchConnectionRequests(email, role, uid);

        if (role === "Professional") {
          const stats = await getOrgClientStats(email);
          if (stats) setOrgClientStats(stats);
        }
      } catch (err) {
        console.error("Error loading user:", err);
      }
    }

    fetchUserInfo();
  }, [fetchConnectionRequests, fetchUserLinks]);

  useEffect(() => {
    if (!requesterEmail || !requesterId || !signedInAs) return;

    const normalizedEmail = String(requesterEmail || "")
      .trim()
      .toLowerCase();

    const refreshRequests = () =>
      fetchConnectionRequests(normalizedEmail, signedInAs, requesterId);

    const subscribeToRequests = (query, variables) =>
      client
        .graphql({
          authMode: "userPool",
          query,
          variables,
        })
        .subscribe({
          next: refreshRequests,
          error: (error) => {
            console.warn("Connection request subscription error:", error);
          },
        });

    const subscriptions = [
      subscribeToRequests(onCreateConnectionRequest, { requesterId }),
      subscribeToRequests(onUpdateConnectionRequest, { requesterId }),
      subscribeToRequests(onDeleteConnectionRequest, { requesterId }),
      subscribeToRequests(onCreateConnectionRequest, {
        targetUserId: requesterId,
      }),
      subscribeToRequests(onUpdateConnectionRequest, {
        targetUserId: requesterId,
      }),
      subscribeToRequests(onDeleteConnectionRequest, {
        targetUserId: requesterId,
      }),
      subscribeToRequests(onCreateConnectionRequest, {
        targetEmail: normalizedEmail,
      }),
      subscribeToRequests(onUpdateConnectionRequest, {
        targetEmail: normalizedEmail,
      }),
      subscribeToRequests(onDeleteConnectionRequest, {
        targetEmail: normalizedEmail,
      }),
    ];

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [fetchConnectionRequests, requesterEmail, requesterId, signedInAs]);

  const handleSendRequest = async () => {
    if (sendingRequestRef.current || isSendingRequest) return;

    const recipientEmails = Array.from(
      new Set(
        targetEmail
          .split(/[;,\n]/)
          .map((email) => email.trim().toLowerCase())
          .filter(Boolean),
      ),
    );

    if (recipientEmails.length === 0 || !direction) {
      setNotification("Please fill in all fields.");
      return;
    }

    if (recipientEmails.some((email) => !/^\S+@\S+\.\S+$/.test(email))) {
      setNotification("Enter one or more valid email addresses.");
      return;
    }

    if (direction === "toProfessional" && signedInAs === "Professional") {
      setNotification(
        "Professionals cannot send requests to other professionals.",
      );
      return;
    }

    if (direction === "toIndividual" && signedInAs !== "Professional") {
      setNotification("Only professionals can send requests to individuals.");
      return;
    }

    sendingRequestRef.current = true;
    setIsSendingRequest(true);
    try {
      // ✅ Fetch current user's name details
      const attrs = await fetchUserAttributes();
      const firstName = attrs["given_name"] || "";
      const lastName = attrs["family_name"] || "";
      const requesterOrgId = attrs["custom:organizationId"] || "";
      let requestOrganizationId = null;

      if (signedInAs === "Professional") {
        try {
          const paymentScope = await checkOrgClientLimit({
            professionalEmail: requesterEmail,
            professionalId: attrs.sub,
            organizationId: requesterOrgId || null,
          });
          if (paymentScope.organizationId) {
            requestOrganizationId = paymentScope.organizationId;
          }
        } catch (err) {
          console.error("❌ Client limit error:", err.message);
          setNotification(err.message); // show message to user
          return; // stop the rest of the send logic
        }
      }

      for (const recipientEmail of recipientEmails) {
        const input = {
          requesterId,
          requesterEmail,
          requesterFirstName: firstName,
          requesterLastName: lastName,
          targetEmail: recipientEmail,
          status: "pending",
          direction,
          ...(requestOrganizationId
            ? { organizationId: requestOrganizationId }
            : {}),
        };

        try {
          const targetUserRes = await client.graphql({
            query: listGeneralInformations,
            variables: {
              filter: { Email: { eq: recipientEmail } },
              limit: 1,
            },
          });
          let targetUserId =
            targetUserRes?.data?.listGeneralInformations?.items?.[0]?.userId ||
            null;

          if (!targetUserId) {
            const profilesRes = await client.graphql({
              query: listGeneralInformations,
              variables: { limit: 1000 },
            });
            const matchingProfile = (
              profilesRes?.data?.listGeneralInformations?.items || []
            ).find(
              (profile) =>
                String(profile?.Email || "")
                  .trim()
                  .toLowerCase() === recipientEmail,
            );
            targetUserId = matchingProfile?.userId || null;
          }

          if (targetUserId) input.targetUserId = targetUserId;
        } catch (lookupErr) {
          console.warn(
            "Could not resolve connection recipient user ID; sending invitation by email.",
            lookupErr,
          );
        }

        await client.graphql({
          query: createConnectionRequest,
          variables: { input },
        });

        try {
          await postEmailNotification({
            to: recipientEmail,
            subject:
              "You have a new connection request on the Wholistic Health App®",
            body: `Hi, ${firstName} ${lastName} wants to collaborate with you on the Wholistic Health App®. This app allows individuals to complete an assessment, receive immediate results, create a health plan, track progress, collaborate with their health team, and join health communities. If you already have an account, login, click on CoConnect™ then Professional Connections to respond to their request. If you don't have an account, go to http://outcomesexcellence.org/ and click Access WHApp® to create an account.`,
            includeButton: true,
            buttonUrl: "https://wholistichealthapp.com/",
            buttonText: "Respond to their request",
          });
        } catch (emailErr) {
          console.error("Failed to send connection request email:", emailErr);
        }
      }

      await fetchConnectionRequests(requesterEmail, signedInAs, requesterId);
      setNotification(
        `Connection request${recipientEmails.length === 1 ? "" : "s"} sent!`,
      );
      setTargetEmail("");
    } catch (err) {
      console.error("Error sending request:", err);
      setNotification("Failed to send request.");
    } finally {
      sendingRequestRef.current = false;
      setIsSendingRequest(false);
    }
  };

  const handleUnlink = async (link) => {
    try {
      const unlinkedAt = new Date().toISOString();
      await client.graphql({
        query: updateUserLink,
        variables: {
          input: { id: link.id, unlinkedAt },
        },
      });

      const attributes = await fetchUserAttributes();
      const isProfessional = attributes.sub === link.professionalId;
      await client.graphql({
        query: createConnectionRequest,
        variables: {
          input: {
            requesterId: attributes.sub,
            requesterFirstName: attributes.given_name || "",
            requesterLastName: attributes.family_name || "",
            requesterEmail: attributes.email,
            targetEmail: isProfessional
              ? link.clientEmail
              : link.professionalEmail,
            targetUserId: isProfessional ? link.clientId : link.professionalId,
            ...(link.organizationId
              ? { organizationId: link.organizationId }
              : {}),
            status: "unlinked",
            direction: isProfessional ? "toIndividual" : "toProfessional",
          },
        },
      });

      // Keep the row (history), but it won't show under "Current Connections".
      setUserLinks((prev) =>
        prev.map((link) =>
          link.id === linkToUnlink.id ? { ...link, unlinkedAt } : link,
        ),
      );
      await fetchConnectionRequests(requesterEmail, signedInAs, requesterId);
    } catch (err) {
      console.error("Error unlinking:", err);
      alert("Failed to unlink.");
    }
  };
  const [generalInformation, setListGeneralInformation] = useState([]);

  useEffect(() => {
    const fetchGeneralInformation = async () => {
      try {
        const res = await client.graphql({
          query: listGeneralInformations,
        });
        const items = res?.data?.listGeneralInformations?.items || [];
        setListGeneralInformation(items);
      } catch (err) {
        console.error("Error fetching general information:", err);
      }
    };

    fetchGeneralInformation();
  }, []);
  const getUserInfoByEmail = (email) => {
    if (!generalInformation || generalInformation.length === 0) return {};

    const emailNorm = String(email || "")
      .trim()
      .toLowerCase();
    if (!emailNorm) return {};

    return (
      generalInformation.find(
        (u) =>
          String(u.email || u.Email || u.userEmail || "")
            .trim()
            .toLowerCase() === emailNorm,
      ) || {}
    );
  };

  const getUserInfoById = (userId) => {
    if (!generalInformation || generalInformation.length === 0) return {};
    return (
      generalInformation.find(
        (u) => u.userId === userId || u.owner === userId,
      ) || {}
    );
  };
  return (
    <>
      <div
        ref={containerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 20,
          width: "100%",
        }}
      >
        {/* Section 1: Send Request */}
        <div style={containerStyle}>
          <img
            src={(process.env.PUBLIC_URL || "") + "/Icon-PC-Blue.png"}
            alt="Logo"
            style={{
              width: "100%",
              maxWidth: 200,
              height: "auto",
              display: "block",
              margin: "0 auto 30px auto",
            }}
          />
          {orgClientStats && (
            <p
              style={{ marginBottom: 10, fontWeight: "600", color: "#0067B3" }}
            >
              Clients: {orgClientStats.current} / {orgClientStats.max}
            </p>
          )}

          <h2 style={titleStyle}>Send Connection Request</h2>

          <div
            style={{
              backgroundColor: "#e0f0ff",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 16,
              fontSize: 13,
              color: "#0067B3",
            }}
          >
            <strong>Important:</strong> If your request is accepted, this will
            allow the professional to see your health plan under
            &quot;Plan&quot;, your progress on your health plan under
            &quot;Progress&quot;, and your assessment, health plan, and lab
            reports under &quot;Explore Results&quot;.
          </div>

          <label style={labelStyle}>Email:</label>
          <input
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            placeholder="Enter email"
            style={inputStyle}
          />

          <label style={labelStyle}>Request Type:</label>
          <input
            type="text"
            value={
              direction === "toProfessional"
                ? "To Professional"
                : direction === "toIndividual"
                  ? "To Individual"
                  : "Detecting..."
            }
            style={{
              ...inputStyle,
              backgroundColor: "#f0f0f0",
              cursor: "not-allowed",
            }}
            disabled
          />

          <button
            onClick={handleSendRequest}
            style={buttonStyle}
            disabled={isSendingRequest}
          >
            {isSendingRequest ? "Sending..." : "Send Request"}
          </button>

          {notification && <div style={notificationStyle}>{notification}</div>}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 20,
            width: "100%",
          }}
        >
          {/* Section 2: Linked Users */}
          <div style={containerStyle2}>
            <h3 style={titleStyle}>Your Current Connections</h3>
            {userLinks.filter((l) => !l.unlinkedAt).length === 0 ? (
              <p style={emptyStyle}>No Connections.</p>
            ) : (
              userLinks
                .filter((l) => !l.unlinkedAt)
                .map((link) => {
                  const profInfo = getUserInfoById(link.professionalId) || {};
                  const clientInfo = getUserInfoById(link.clientId) || {};

                  const isCurrentUserProfessional =
                    link.professionalId === requesterId;

                  const connectedEmail = isCurrentUserProfessional
                    ? clientInfo.Email || link.clientEmail
                    : profInfo.Email || link.professionalEmail;

                  const connectedFirstName = isCurrentUserProfessional
                    ? link.clientFirstName || clientInfo.FirstName || "Unknown"
                    : link.professionalFirstName ||
                      profInfo.FirstName ||
                      "Unknown";

                  const connectedLastName = isCurrentUserProfessional
                    ? link.clientLastName || clientInfo.LastName || ""
                    : link.professionalLastName || profInfo.LastName || "";

                  const connectedRole = isCurrentUserProfessional
                    ? "Client"
                    : link.professionalRole ||
                      (professionalRoles[link.professionalId] &&
                      professionalRoles[link.professionalId] !== "Unknown"
                        ? professionalRoles[link.professionalId]
                        : profInfo.ProfessionalRole) ||
                      "Unknown";

                  return (
                    <div key={link.id} style={summaryRowStyle}>
                      <div style={connectionGridStyle}>
                        <div style={summaryCellStyle}>
                          <strong>Email</strong>
                          <div style={summaryValueStyle}>{connectedEmail}</div>
                        </div>

                        <div style={summaryCellStyle}>
                          <strong>Name</strong>
                          <div style={summaryValueStyle}>
                            {connectedFirstName} {connectedLastName}
                          </div>
                        </div>

                        <div style={summaryCellStyle}>
                          <strong>Role</strong>
                          <div style={summaryValueStyle}>{connectedRole}</div>
                        </div>

                        <div style={summaryActionStyle}>
                          <button
                            onClick={() => setLinkToUnlink(link)}
                            style={unlinkButtonStyle}
                          >
                            Unlink
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
          <div style={containerStyle2}>
            <h3 style={titleStyle}>Pending Connection Requests</h3>
            {requests.length === 0 ? (
              <p style={emptyStyle}>No pending requests.</p>
            ) : (
              requests.map((req) => {
                const isOutgoing =
                  String(req.requesterEmail || "")
                    .trim()
                    .toLowerCase() ===
                  String(requesterEmail || "")
                    .trim()
                    .toLowerCase();
                const isAwaitingProfessional = req.status === "clientAccepted";
                const canConfirmAsProfessional =
                  signedInAs === "Professional" &&
                  isOutgoing &&
                  isAwaitingProfessional;
                const isClientViewingAwaiting =
                  signedInAs !== "Professional" &&
                  !isOutgoing &&
                  isAwaitingProfessional;

                return (
                  <div key={req.id} style={summaryRowStyle}>
                    <div style={requestGridStyle}>
                      <div style={summaryCellStyle}>
                        <strong>{isOutgoing ? "To" : "From"}</strong>
                        <div style={summaryValueStyle}>
                          {isOutgoing
                            ? req.targetEmail
                            : `${req.requesterFirstName} ${req.requesterLastName} (${req.requesterEmail})`}
                        </div>
                      </div>

                      <div style={summaryCellStyle}>
                        <strong>Type</strong>
                        <div style={summaryValueStyle}>
                          {isOutgoing
                            ? req.direction === "toProfessional"
                              ? "You to Professional"
                              : "You to Client"
                            : req.direction === "toProfessional"
                              ? "Client to You"
                              : "Professional to You"}
                        </div>
                      </div>

                      <div style={summaryCellStyle}>
                        <strong>Status</strong>
                        <div style={summaryValueStyle}>
                          {isClientViewingAwaiting
                            ? "Awaiting Professional Confirmation"
                            : canConfirmAsProfessional
                              ? "Client Accepted"
                              : isOutgoing
                                ? "Outgoing Request"
                                : "Awaiting Response"}
                        </div>
                      </div>

                      <div style={summaryActionStyle}>
                        {isClientViewingAwaiting ? (
                          <em style={{ color: "gray" }}>
                            Awaiting Professional Confirmation
                          </em>
                        ) : !isOutgoing || canConfirmAsProfessional ? (
                          <>
                            <button
                              style={acceptBtn}
                              onClick={() => respondToRequest(req, true)}
                            >
                              Accept
                            </button>
                            <button
                              style={declineBtn}
                              onClick={() => respondToRequest(req, false)}
                            >
                              Decline
                            </button>
                          </>
                        ) : (
                          <button
                            style={declineBtn}
                            onClick={() => cancelRequest(req.id)}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div style={containerStyle2}>
            <h3 style={titleStyle}>Connection Request History</h3>
            {historyRequests.length === 0 ? (
              <p style={emptyStyle}>No past requests.</p>
            ) : (
              historyRequests.map((req) => {
                const isOutgoing = req.requesterEmail === requesterEmail;
                const dateSent = new Date(req.createdAt).toLocaleDateString(
                  "en-US",
                );
                const professionalInfo = getUserInfoByEmail(req.targetEmail);

                return (
                  <div key={req.id} style={summaryRowStyle}>
                    <div style={historyGridStyle}>
                      <div style={summaryCellStyle}>
                        <strong>{isOutgoing ? "To" : "From"}</strong>
                        <div style={summaryValueStyle}>
                          {isOutgoing
                            ? req.targetEmail
                            : `${req.requesterFirstName} ${req.requesterLastName} (${req.requesterEmail})`}
                        </div>
                      </div>

                      <div style={summaryCellStyle}>
                        <strong>Type</strong>
                        <div style={summaryValueStyle}>
                          {isOutgoing
                            ? req.direction === "toProfessional"
                              ? `You to ${
                                  professionalInfo.ProfessionalRole ||
                                  "Professional"
                                }`
                              : "You to Individual"
                            : req.direction === "toProfessional"
                              ? "Client to You"
                              : "Professional to You"}
                        </div>
                      </div>

                      <div style={summaryCellStyle}>
                        <strong>Date Sent</strong>
                        <div style={summaryValueStyle}>{dateSent}</div>
                      </div>

                      <div style={summaryCellStyle}>
                        <strong>Status</strong>
                        <div style={summaryValueStyle}>
                          {req.status.charAt(0).toUpperCase() +
                            req.status.slice(1).toLowerCase()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {linkToUnlink &&
        typeof document !== "undefined" &&
        createPortal(
          <div style={modalOverlay} onClick={() => setLinkToUnlink(null)}>
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: modalCenterX,
                transform: "translate(-50%, -50%)",
                width: "min(420px, calc(100vw - 32px))",
                boxSizing: "border-box",
              }}
            >
              <div style={modalContent} onClick={(e) => e.stopPropagation()}>
                <h3>Confirm Unlink</h3>
                <p>
                  Are you sure you want to unlink{" "}
                  <strong>
                    {linkToUnlink.clientFirstName} {linkToUnlink.clientLastName}
                  </strong>{" "}
                  from{" "}
                  <strong>
                    {linkToUnlink.professionalFirstName}{" "}
                    {linkToUnlink.professionalLastName}
                  </strong>
                  ?
                </p>
                <div style={modalButtons}>
                  <button
                    style={modalButtonConfirm}
                    onClick={async () => {
                      await handleUnlink(linkToUnlink);
                      setLinkToUnlink(null);
                    }}
                  >
                    Yes, Unlink
                  </button>
                  <button
                    style={modalButtonCancel}
                    onClick={() => setLinkToUnlink(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// ===== STYLES =====

const containerStyle = {
  width: 350,
  minWidth: 300,
  padding: 24,
  borderRadius: 12,
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
};

const titleStyle = {
  fontSize: 20,
  fontWeight: "bold",
  marginBottom: 16,
  color: "#0067B3",
};

const labelStyle = {
  marginTop: 16,
  fontWeight: "600",
  display: "block",
  fontSize: 14,
  textAlign: "left",
};

const inputStyle = {
  width: "100%",
  padding: 10,
  fontSize: 14,
  borderRadius: 8,
  border: "1.5px solid #ccc",
  marginTop: 6,
  boxSizing: "border-box",
};

const buttonStyle = {
  marginTop: 24,
  width: "100%",
  backgroundColor: "#0067B3",
  color: "white",
  fontWeight: "700",
  fontSize: 16,
  borderRadius: 8,
  padding: "12px 0",
  border: "none",
  cursor: "pointer",
  userSelect: "none",
  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
};

const notificationStyle = {
  marginTop: 16,
  backgroundColor: "#e0f0ff",
  padding: 10,
  borderRadius: 8,
  color: "#0067B3",
  textAlign: "center",
  fontWeight: "600",
};

const linkBoxStyle = {
  backgroundColor: "#f9f9f9",
  padding: 16,
  borderRadius: 10,
  marginBottom: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
  width: "100%",
  boxSizing: "border-box",
};

const unlinkButtonStyle = {
  backgroundColor: "#8C1456",
  color: "white",
  border: "none",
  padding: "8px 12px",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: 14,
};

const emptyStyle = {
  fontStyle: "italic",
  color: "#333",
};
const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalContent = {
  backgroundColor: "#ffffff",
  border: "2px solid #0067B3",
  borderRadius: "16px",
  padding: "30px 24px",
  width: "100%",
  textAlign: "center",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
  animation: "fadeInScale 0.3s ease-in-out",
};

const modalButtons = {
  display: "flex",
  justifyContent: "center",
  gap: "12px",
  marginTop: "20px",
  flexWrap: "wrap",
};

const modalButtonConfirm = {
  backgroundColor: "#0067B3",
  color: "#fff",
  border: "none",
  borderRadius: "999px",
  padding: "10px 20px",
  width: "140px",
  fontWeight: "bold",
  cursor: "pointer",
};

const modalButtonCancel = {
  backgroundColor: "#ccc",
  color: "#000",
  border: "none",
  borderRadius: "999px",
  padding: "10px 20px",
  width: "140px",
  fontWeight: "bold",
  cursor: "pointer",
};
const acceptBtn = {
  backgroundColor: "#2F5D50",
  color: "white",
  border: "none",
  padding: "6px 12px",
  marginRight: 8,
  borderRadius: 6,
  cursor: "pointer",
};

const declineBtn = {
  backgroundColor: "#8C1456",
  color: "white",
  border: "none",
  padding: "6px 12px",
  borderRadius: 6,
  cursor: "pointer",
};
